import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext.jsx';
import { usePersistentState } from '../hooks/usePersistentState.js';
import { api } from '../lib/api.js';
import { SHIFT_NAME_KEY, SHIFT_QTY_KEY } from '../lib/constants.js';
import { formatMoney } from '../lib/format.js';
import { cx } from '../lib/util.js';
import { Btn, Field, MonoInput, PageHeading, Tag } from '../components/ui.jsx';
import { Modal } from '../components/Modal.jsx';

export default function ShiftCalculator() {
  const { snapshot, loading, pushToast, refresh } = useApp();
  const navigate = useNavigate();

  // Ticket + name are cached so refreshing, switching tabs, or navigating away
  // and back restores an in-progress shift instead of wiping it.
  const [qty, setQty] = usePersistentState(SHIFT_QTY_KEY, {});
  const [name, setName] = usePersistentState(SHIFT_NAME_KEY, '');
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const items = snapshot.items || [];

  const lines = useMemo(
    () =>
      items
        .filter((it) => (qty[it.id] || 0) > 0)
        .map((it) => ({
          id: it.id,
          name: it.name,
          qty: qty[it.id],
          rate: it.payout_rate,
          lineTotal: it.payout_rate * qty[it.id],
        })),
    [items, qty]
  );

  const total = lines.reduce((sum, l) => sum + l.lineTotal, 0);
  const itemCount = lines.reduce((sum, l) => sum + l.qty, 0);
  const canSubmit = Boolean(name.trim()) && itemCount > 0 && !submitting;

  const bump = (id, delta) =>
    setQty((q) => ({ ...q, [id]: Math.max(0, (q[id] || 0) + delta) }));

  const handleClockOut = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await api.clockOut({
        employee_name: name.trim(),
        items_sold: lines.map((l) => ({ id: l.id, qty: l.qty })),
      });
      setReceipt(res);
      setQty({});
      refresh();
    } catch (err) {
      pushToast(err.message, 'ember');
    } finally {
      setSubmitting(false);
    }
  };

  const continueToAudit = () => {
    setReceipt(null);
    setName('');
    navigate('/audit');
  };

  return (
    <div>
      <PageHeading
        eyebrow="Register"
        title="Shift Calculator"
        live
        actions={
          <Tag tone={itemCount > 0 ? 'teal' : 'ember'}>
            {itemCount} item{itemCount === 1 ? '' : 's'} on ticket
          </Tag>
        }
      />

      {loading && items.length === 0 ? (
        <div className="border border-edge bg-surface p-10 text-center font-mono text-xs uppercase tracking-widest text-dim">
          Loading register…
        </div>
      ) : items.length === 0 ? (
        <div className="border border-edge bg-surface p-10 text-center font-mono text-xs uppercase tracking-widest text-dim">
          No items configured yet — ask a manager to add inventory.
        </div>
      ) : (
        <form onSubmit={handleClockOut} className="grid gap-6 lg:grid-cols-[1fr_340px]">
          <div className="order-2 space-y-3 lg:order-1">
            {items.map((it) => {
              const count = qty[it.id] || 0;
              const out = it.current_stock <= 0;
              return (
                <div
                  key={it.id}
                  className={cx(
                    'flex items-center justify-between gap-4 border border-edge bg-panel p-4',
                    count > 0 && 'border-neon/40'
                  )}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-mono text-sm font-bold text-snow">{it.name}</p>
                      {out && <Tag tone="ember">Out of stock</Tag>}
                    </div>
                    <p className="font-mono text-xs text-dim">
                      Payout{' '}
                      <span className={out ? 'text-dim' : 'text-neon'}>
                        {formatMoney(it.payout_rate)}
                      </span>
                      <span className="mx-2 text-edge">|</span>
                      <span className={out ? 'text-ember' : 'text-mist'}>
                        {it.current_stock} in stock
                      </span>
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <button
                      type="button"
                      aria-label={`Remove one ${it.name}`}
                      onClick={() => bump(it.id, -1)}
                      disabled={count === 0}
                      className="grid h-11 w-11 place-items-center border border-ember/60 font-mono text-2xl text-ember transition-colors hover:bg-ember/10 disabled:opacity-30"
                    >
                      −
                    </button>
                    <input
                      type="text"
                      inputMode="numeric"
                      aria-label={`Quantity for ${it.name}`}
                      value={count}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setQty((q) => ({ ...q, [it.id]: digits === '' ? 0 : Number(digits) }));
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '') {
                          setQty((q) => ({ ...q, [it.id]: 0 }));
                        }
                      }}
                      className="tnum w-14 border-none bg-transparent text-center font-mono text-2xl font-bold text-snow outline-none focus:bg-void focus:outline focus:outline-1 focus:outline-neon"
                    />
                    <button
                      type="button"
                      aria-label={`Add one ${it.name}`}
                      onClick={() => bump(it.id, 1)}
                      className="grid h-11 w-11 place-items-center border border-neon/60 font-mono text-2xl text-neon transition-colors hover:bg-neon/10"
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <aside className="order-1 h-fit lg:order-2 lg:sticky lg:top-24">
            <div className="border-2 border-neon bg-surface p-4 shadow-neon-teal">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-mist">
                Total owed
              </p>
              <p className="tnum mt-1 truncate font-mono text-4xl font-bold text-neon glow-teal">
                {formatMoney(total)}
              </p>

              {lines.length > 0 && (
                <ul className="mt-4 space-y-1 border-t border-edge pt-3">
                  {lines.map((l) => (
                    <li
                      key={l.id}
                      className="flex items-baseline justify-between gap-2 font-mono text-xs text-mist"
                    >
                      <span className="truncate">
                        {l.name} <span className="text-snow">×{l.qty}</span>
                      </span>
                      <span className="tnum shrink-0 text-snow">{formatMoney(l.lineTotal)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-4 space-y-4 border border-edge bg-panel p-4">
              <Field label="Employee name">
                <MonoInput
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. JOHNNY B."
                  maxLength={40}
                />
              </Field>
              <Btn type="submit" variant="teal" size="lg" disabled={!canSubmit} className="w-full">
                {submitting ? 'Clocking out…' : 'Clock Out'}
              </Btn>
              <p className="font-mono text-[10px] uppercase tracking-widest text-dim">
                Clock-out writes your shift, lowers live stock, and opens the
                inventory audit.
              </p>
            </div>
          </aside>
        </form>
      )}

      <Modal
        open={Boolean(receipt)}
        onClose={continueToAudit}
        title="Shift Clocked Out"
        accent="teal"
        footer={
          <>
            <Btn variant="ghost" onClick={continueToAudit}>
              Ring Up Again
            </Btn>
            <Btn variant="teal" onClick={continueToAudit}>
              Done — Inventory Audit
            </Btn>
          </>
        }
      >
        {receipt && (
          <div className="space-y-4">
            <div className="border border-edge bg-void p-4 text-center">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mist">
                Final transfer amount
              </p>
              <p className="tnum mt-1 font-mono text-5xl font-bold text-neon glow-teal">
                {formatMoney(receipt.total_owed)}
              </p>
              <p className="mt-1 font-mono text-xs text-dim">
                {receipt.employee_name.toUpperCase()} · {itemCountFallback(receipt)}
              </p>
            </div>
            <ul className="space-y-1.5 border border-edge bg-surface p-4">
              {receipt.items_sold.map((it) => (
                <li
                  key={it.id}
                  className="flex items-baseline justify-between gap-2 font-mono text-xs"
                >
                  <span className="text-mist">
                    {it.name} <span className="text-snow">×{it.qty}</span>
                  </span>
                  <span className="tnum text-snow">{formatMoney(it.line_total)}</span>
                </li>
              ))}
            </ul>
            <p className="font-mono text-[10px] uppercase tracking-widest text-dim">
              Continue to the physical inventory audit to sync live stock counts.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

function itemCountFallback(receipt) {
  const n = (receipt.items_sold || []).reduce((s, it) => s + it.qty, 0);
  return `${n} item${n === 1 ? '' : 's'} sold`;
}